/**
 * Registre unique des permissions (TODO annexe A, ADR-002/012/013).
 *
 * Source de vérité partagée par l'API (middleware, seed, `rbac:sync`) et
 * les deux portails React (`useCan`, menus). Ajouter une permission ici,
 * puis lancer `node ace rbac:sync` : elle apparaît dans la matrice des profils.
 *
 * Convention : `module.action` en minuscules. Ne jamais renommer un code
 * existant (il est stocké en base) : déprécier puis en créer un nouveau.
 */
export const PERMISSION_MODULES = {
    users: 'Utilisateurs',
    roles: 'Profils',
    concours: 'Concours',
    forms: 'Formulaires',
    candidates: 'Candidats',
    candidature: 'Candidatures',
    documents: 'Pièces',
    payment: 'Paiements',
    payment_methods: 'Moyens de paiement',
    corrections: 'Corrections',
    selection: 'Sélection',
    notes: 'Notes',
    results: 'Résultats',
    convocations: 'Documents officiels',
    notifications: 'Notifications',
    reports: 'Rapports',
    audit: 'Audit',
    settings: 'Système',
};
const p = (code, module, label, sensitive = false) => ({ code, module, label, sensitive });
export const PERMISSIONS = [
    // Utilisateurs internes
    p('users.view', 'users', 'Consulter les utilisateurs'),
    p('users.create', 'users', 'Inviter un utilisateur'),
    p('users.update', 'users', 'Modifier un utilisateur'),
    p('users.disable', 'users', 'Activer / désactiver un compte', true),
    p('users.reset_mfa', 'users', 'Réinitialiser la double authentification', true),
    p('users.assign_roles', 'users', 'Attribuer des profils', true),
    // Profils
    p('roles.view', 'roles', 'Consulter les profils'),
    p('roles.create', 'roles', 'Créer un profil', true),
    p('roles.update', 'roles', 'Modifier les permissions d’un profil', true),
    p('roles.delete', 'roles', 'Supprimer un profil', true),
    // Concours
    p('concours.view', 'concours', 'Consulter les concours'),
    p('concours.create', 'concours', 'Créer un concours'),
    p('concours.update', 'concours', 'Modifier un concours'),
    p('concours.publish', 'concours', 'Publier un concours'),
    p('concours.open', 'concours', 'Ouvrir les dépôts'),
    p('concours.suspend', 'concours', 'Suspendre un concours', true),
    p('concours.close', 'concours', 'Clôturer les dépôts'),
    p('concours.extend', 'concours', 'Prolonger les dépôts', true),
    p('concours.derogation', 'concours', 'Accorder une dérogation à la clôture', true),
    p('concours.archive', 'concours', 'Archiver un concours'),
    // Formulaires
    p('forms.view', 'forms', 'Consulter les formulaires'),
    p('forms.create', 'forms', 'Créer un formulaire'),
    p('forms.update', 'forms', 'Modifier un formulaire'),
    p('forms.publish', 'forms', 'Publier une version de formulaire', true),
    // Candidats
    p('candidates.view', 'candidates', 'Consulter les profils candidats'),
    p('candidates.view_user_number', 'candidates', 'Voir le matricule utilisateur'),
    p('candidates.duplicates.review', 'candidates', 'Traiter les doublons potentiels', true),
    // Candidatures
    p('candidature.view', 'candidature', 'Consulter les candidatures'),
    p('candidature.review', 'candidature', 'Instruire un dossier'),
    p('candidature.request_complement', 'candidature', 'Demander un complément'),
    p('candidature.assign', 'candidature', 'Répartir les dossiers entre les agents'),
    p('candidature.decide', 'candidature', 'Proposer une décision de recevabilité', true),
    p('candidature.validate', 'candidature', 'Valider une décision de recevabilité', true),
    p('candidature.correct_after_submit', 'candidature', 'Demander une correction après dépôt', true),
    p('candidature.cancel', 'candidature', 'Annuler une candidature', true),
    // Pièces
    p('documents.download', 'documents', 'Télécharger les pièces', true),
    p('documents.review', 'documents', 'Accepter / refuser une pièce'),
    // Paiements
    p('payment.view', 'payment', 'Consulter les paiements'),
    p('payment.reconcile', 'payment', 'Rapprocher les paiements', true),
    p('payment.manual_entry', 'payment', 'Saisie manuelle d’un paiement', true),
    p('payment.refund', 'payment', 'Rembourser un paiement', true),
    p('payment.export', 'payment', 'Exporter les transactions', true),
    // Moyens de paiement (ADR-002)
    p('payment_methods.view', 'payment_methods', 'Consulter les moyens de paiement'),
    p('payment_methods.manage', 'payment_methods', 'Gérer les moyens de paiement', true),
    // Corrections à double validation (ADR-013)
    p('corrections.view', 'corrections', 'Consulter les demandes de correction'),
    p('corrections.approve', 'corrections', 'Valider une demande de correction', true),
    // Sélection
    p('selection.view', 'selection', 'Consulter les étapes de sélection'),
    p('selection.configure', 'selection', 'Configurer étapes, disciplines et barèmes'),
    p('selection.unlock_scales', 'selection', 'Déverrouiller un barème', true),
    p('selection.rules.validate', 'selection', 'Valider les règles de passage', true),
    p('evaluators.assign', 'selection', 'Affecter les évaluateurs'),
    p('centers.manage', 'selection', 'Gérer les centres et salles'),
    // Notes
    p('notes.view', 'notes', 'Consulter les notes'),
    p('notes.enter', 'notes', 'Saisir les notes'),
    p('notes.submit', 'notes', 'Soumettre les notes'),
    p('notes.correct', 'notes', 'Corriger une note soumise', true),
    p('notes.validate', 'notes', 'Valider les relevés de notes', true),
    p('notes.unlock', 'notes', 'Déverrouiller un relevé validé', true),
    // Résultats
    p('results.view', 'results', 'Consulter les résultats'),
    p('results.calculate', 'results', 'Calculer les classements'),
    p('results.approve', 'results', 'Approuver les résultats', true),
    p('results.publish', 'results', 'Publier les résultats', true),
    p('results.rectify', 'results', 'Demander une rectification', true),
    // Documents officiels
    p('convocations.generate', 'convocations', 'Générer les convocations'),
    p('convocations.send', 'convocations', 'Envoyer les convocations'),
    p('pv.generate', 'convocations', 'Générer les procès-verbaux'),
    // Notifications
    p('notifications.send', 'notifications', 'Envoyer des notifications groupées'),
    p('notifications.templates.manage', 'notifications', 'Gérer les modèles de notification'),
    // Rapports
    p('reports.view', 'reports', 'Consulter les tableaux de bord'),
    p('reports.export', 'reports', 'Exporter des rapports', true),
    // Audit
    p('audit.view', 'audit', 'Consulter le journal d’audit'),
    p('audit.export', 'audit', 'Exporter le journal d’audit', true),
    // Système
    p('settings.manage', 'settings', 'Paramètres techniques', true),
    p('settings.retention.manage', 'settings', 'Politiques de conservation', true),
    p('notification_providers.manage', 'settings', 'Fournisseurs email / SMS', true),
];
export const PERMISSION_CODES = PERMISSIONS.map((perm) => perm.code);
const CODE_SET = new Set(PERMISSION_CODES);
export function isPermissionCode(value) {
    return CODE_SET.has(value);
}
/** Permissions groupées par module, dans l'ordre du registre (pour la matrice UI). */
export function permissionsByModule() {
    const grouped = {};
    for (const perm of PERMISSIONS) {
        ;
        (grouped[perm.module] ??= []).push(perm);
    }
    return grouped;
}
//# sourceMappingURL=permissions.js.map